import { useState } from "react";
import { ScrollText, Gavel, ChevronDown, ChevronUp } from "lucide-react";
import { StrikeDialog } from "./StrikeDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const EventRow = ({ ev, onStrike }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="px-4 py-3 space-y-2" data-testid={`event-row-${ev.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold font-mono" data-testid={`event-user-${ev.id}`}>{ev.user_email}</span>
            {ev.user_name && <span className="text-xs text-muted-foreground">({ev.user_name})</span>}
            <span className="text-[10px] font-mono text-muted-foreground">{fmt(ev.created_at)}</span>
            {ev.strike && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border border-[#FF3B30]/50 text-[#FF3B30] rounded-sm"
                data-testid={`event-strike-badge-${ev.id}`}>Strike #{ev.strike.strike_number}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">To: {ev.business_name} &lt;{ev.business_email}&gt;</p>
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`event-flags-${ev.id}`}>
            {ev.hits.map((h) => (
              <span key={h.flag_id} title={h.reason}
                className="text-[10px] font-semibold px-2 py-0.5 border border-[#FFCC00]/40 bg-[#FFCC00]/10 text-[#FFCC00] rounded-sm">
                {h.name}
              </span>
            ))}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={() => setOpen((o) => !o)} data-testid={`event-expand-${ev.id}`} title={open ? "Hide message" : "Show message"}
            className="w-8 h-8 flex items-center justify-center border border-border text-muted-foreground hover:text-white hover:border-white rounded-sm transition-colors duration-200">
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {!ev.strike && <StrikeDialog event={ev} onIssued={onStrike} />}
        </div>
      </div>
      {open && (
        <div className="space-y-2" data-testid={`event-detail-${ev.id}`}>
          <pre className="text-xs whitespace-pre-wrap font-sans bg-background border border-border rounded-sm p-3 text-white/90 leading-relaxed">{ev.message}</pre>
          <ul className="space-y-1">
            {ev.hits.map((h) => (
              <li key={h.flag_id} className="text-xs text-muted-foreground">
                <span className="text-[#FFCC00] font-semibold">{h.name}</span> — {h.reason}
              </li>
            ))}
          </ul>
          {ev.strike && (
            <p className="text-xs text-[#FF3B30]">
              Strike issued {fmt(ev.strike.issued_at)} by {ev.strike.admin_email}{ev.strike.note ? ` — “${ev.strike.note}”` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const ViolationLog = ({ events, onStrike }) => (
  <div className="border border-border bg-[#0A0A0A] rounded-sm p-6 space-y-4" data-testid="violation-log">
    <div>
      <div className="flex items-center gap-2">
        <ScrollText className="w-4 h-4 text-[#FFCC00]" />
        <h2 className="font-heading text-lg font-black tracking-tight">Blocked messages</h2>
      </div>
      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
        Newest first. Expand a row to read the full message, then use <Gavel className="w-3 h-3 inline" /> to issue a strike if it shows malice.
      </p>
    </div>
    {events === null && <p className="text-sm text-muted-foreground animate-pulse">Loading…</p>}
    {events?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="events-empty">No blocked messages yet.</p>}
    {events?.length > 0 && (
      <div className="divide-y divide-border border border-border rounded-sm">
        {events.map((ev) => <EventRow key={ev.id} ev={ev} onStrike={onStrike} />)}
      </div>
    )}
  </div>
);
